import type {
  CraftRoomResponse,
  MaterialResponse,
  CraftProjectResponse,
  ProjectMaterialResponse,
  ProjectCommentResponse,
  CreateMaterialRequest,
  UpdateCraftProjectRequest,
  CreateProjectMaterialRequest,
  UpdateProjectMaterialRequest,
  ProjectTempResponse,
  UpdateTempProjectRequest,
  CraftRoomWithProjectsResponse,
  GetMaterialsResponse,
  MaterialSearchResponse,
  MigrateImagesResponse,
  ContributeMaterialResponse,
  CraftRoomSettingsResponse,
  BatchUpdateResponse,
  BulkDeleteResponse,
  FolderConfigResponse,
  AssignFoldersResponse,
  UpdateFolderConfigResponse,
  ExportCSVResponse,
  ImportCSVResponse,
  MergeMaterialsResponse,
  CreateTempProjectResponse,
  CreateDraftProjectResponse,
  PersonalMaterialDetailResponse,
  CopyMaterialResponse,
  SubmitFieldSuggestionResponse,
  SendFieldSuggestionMessageResponse,
  FieldSuggestionThread,
  MaterialLinkSuggestionQueueResponse,
  ResolveMaterialLinkSuggestionResponse,
  MessageResponse,
  SuccessResponse,
  PendingConfirmationsResponse,
  ResolveFieldSuggestionRequest,
  ChangeLinkRequest,
  SimpleSuccessResponse,
} from '@mrb91002/shared-types'

import { secureFetch, SECURE_API_BASE, API_BASE } from './helpers'

// Re-export shared-types used by consumers that previously imported them from this file
export type { FieldSuggestionMessage, FieldSuggestionThread } from '@mrb91002/shared-types'

// Moved to secure/patterns.ts (uploadTempPatternImage also in secure/images.ts)

// Moved to secure/patterns.ts

// Moved to secure/images.ts

// Moved to secure/profile.ts (also available via secure/images.ts unified upload)

// Moved to secure/profile.ts

// ---- PATTERN MUTATIONS (Published patterns only, NOT for drafts) ----

// Moved to secure/reviews.ts

// ---- USER ACTIONS ----

// Moved to secure/social.ts

// Moved to secure/notifications.ts

// Moved to secure/profile.ts

// Moved to secure/social.ts and secure/notifications.ts

// Moved to secure/patterns.ts

/**
 * Copy personal material to your craft room
 */
export async function copyMaterial(
  sourceMaterialId: string,
  sourceScreenName: string,
  customizations: { quantity?: number; storage_location?: string; notes?: string },
  idToken: string,
): Promise<CopyMaterialResponse> {
  return secureFetch(`${SECURE_API_BASE}/craft-room/materials/copy`, idToken, {
    method: 'POST',
    body: JSON.stringify({
      source_material_id: sourceMaterialId,
      source_screen_name: sourceScreenName,
      customizations,
    }),
  })
}

/**
 * Contribute existing personal material to public database
 */
export async function contributeExistingMaterial(
  materialId: string,
  idToken: string,
  parentMaterialId?: string,
): Promise<ContributeMaterialResponse> {
  const body = parentMaterialId ? { parent_material_id: parentMaterialId } : {}

  return secureFetch(`${SECURE_API_BASE}/materials/${encodeURIComponent(materialId)}/contribute`, idToken, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

/**
 * Migrate material images from temp to processed bucket
 */
export async function migrateMaterialImages(materialId: string, idToken: string): Promise<MigrateImagesResponse> {
  return secureFetch(`${SECURE_API_BASE}/materials/${encodeURIComponent(materialId)}/migrate-images`, idToken, {
    method: 'POST',
  })
}

/**
 * Migrate project images from temp to processed bucket
 */
export async function migrateProjectImages(projectId: string, idToken: string): Promise<MigrateImagesResponse> {
  return secureFetch(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}/migrate-images`, idToken, {
    method: 'POST',
  })
}

// Moved to secure/patterns.ts

// Moved to secure/moderation.ts

// Moved to secure/patterns.ts

// Moved to secure/moderation.ts

// Moved to secure/patterns.ts

// Moved to secure/moderation.ts

// Moved to secure/moderation.ts

// ---- CRAFT ROOM ----

/**
 * Get own craft room with all projects (owner only - uses GSI1)
 */
export async function fetchOwnCraftRoom(idToken: string): Promise<CraftRoomWithProjectsResponse> {
  return secureFetch(`${SECURE_API_BASE}/craft-room`, idToken, {
    method: 'GET',
  })
}

/**
 * Update craft room details
 */
export async function updateCraftRoom(
  roomData: {
    title?: string
    description?: string
    custom_tabs?: string[]
    material_tab_mapping?: Record<string, string>
    room_image_url?: string
  },
  idToken: string,
): Promise<CraftRoomResponse> {
  return secureFetch(`${SECURE_API_BASE}/craft-room`, idToken, {
    method: 'PUT',
    body: JSON.stringify(roomData),
  })
}

/**
 * Get craft room materials (paginated)
 */
export async function fetchMaterials(
  page: number = 1,
  category: string | undefined,
  idToken: string,
): Promise<GetMaterialsResponse> {
  let url = `${SECURE_API_BASE}/materials?page=${page}`
  if (category && category !== 'all') {
    url += `&category=${encodeURIComponent(category)}`
  }
  return secureFetch(url, idToken, { method: 'GET' })
}

/**
 * Get user materials (all materials for inventory checking)
 */
export async function getUserMaterials(idToken: string): Promise<MaterialResponse[]> {
  return secureFetch(`${SECURE_API_BASE}/materials`, idToken, { method: 'GET' })
}

// getUserSettings moved to secure/profile.ts

/**
 * Add material to inventory
 */
export async function addMaterial(materialData: CreateMaterialRequest, idToken: string): Promise<MaterialResponse> {
  return secureFetch(`${SECURE_API_BASE}/materials`, idToken, {
    method: 'POST',
    body: JSON.stringify(materialData),
  })
}

/**
 * Get a single personal material (full record with all fields)
 */
export async function getPersonalMaterial(
  materialId: string,
  idToken: string,
): Promise<PersonalMaterialDetailResponse> {
  return secureFetch(`${SECURE_API_BASE}/materials/${encodeURIComponent(materialId)}`, idToken)
}

/**
 * Update material (private - user's craft room)
 */
export async function updateMaterial(
  materialId: string,
  materialData: Partial<CreateMaterialRequest>,
  idToken: string,
): Promise<MaterialResponse> {
  return secureFetch(`${SECURE_API_BASE}/materials/${encodeURIComponent(materialId)}`, idToken, {
    method: 'PUT',
    body: JSON.stringify(materialData),
  })
}

/**
 * Update public material (moderator only)
 */
export async function updatePublicMaterial(
  materialId: string,
  materialData: Partial<CreateMaterialRequest>,
  idToken: string,
): Promise<MaterialResponse> {
  return secureFetch(`${SECURE_API_BASE}/materials/public/${encodeURIComponent(materialId)}`, idToken, {
    method: 'PUT',
    body: JSON.stringify(materialData),
  })
}

/**
 * Delete material
 */
export async function deleteMaterial(materialId: string, idToken: string): Promise<void> {
  return secureFetch(`${SECURE_API_BASE}/materials/${encodeURIComponent(materialId)}`, idToken, {
    method: 'DELETE',
  })
}

// ---- TEMP PROJECT ROUTES ----

/**
 * Create temp project
 */
export async function createTempProject(idToken: string): Promise<CreateTempProjectResponse> {
  return secureFetch(`${SECURE_API_BASE}/project/temp`, idToken, {
    method: 'POST',
  })
}

/**
 * Get temp project
 */
export async function fetchTempProject(projectId: string, idToken: string): Promise<ProjectTempResponse> {
  return secureFetch(`${SECURE_API_BASE}/project/temp/${encodeURIComponent(projectId)}`, idToken, {
    method: 'GET',
  })
}

/**
 * Update temp project
 */
export async function updateTempProject(
  projectId: string,
  data: UpdateTempProjectRequest,
  idToken: string,
): Promise<ProjectTempResponse> {
  return secureFetch(`${SECURE_API_BASE}/project/temp/${encodeURIComponent(projectId)}`, idToken, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

/**
 * Delete temp project
 */
export async function deleteTempProject(projectId: string, idToken: string): Promise<MessageResponse> {
  return secureFetch(`${SECURE_API_BASE}/project/temp/${encodeURIComponent(projectId)}`, idToken, {
    method: 'DELETE',
  })
}

/**
 * Convert temp project to draft
 */
export async function createDraftProject(
  data: { tempProjectId: string },
  idToken: string,
): Promise<CreateDraftProjectResponse> {
  return secureFetch(`${SECURE_API_BASE}/project/draft`, idToken, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

/**
 * Update draft project
 */
export async function updateDraftProject(
  projectId: string,
  data: UpdateCraftProjectRequest,
  idToken: string,
): Promise<SuccessResponse> {
  return secureFetch(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}/draft`, idToken, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

// ---- LEGACY PROJECT ROUTES ----

/**
 * Get draft project with assembled materials
 */
export async function getDraftProject(projectId: string, idToken: string): Promise<CraftProjectResponse> {
  return secureFetch(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}/draft`, idToken, {
    method: 'GET',
  })
}

/**
 * Publish project (switches GSI4 → GSI3, increments counts)
 */
export async function publishCraftProject(projectId: string, idToken: string): Promise<MessageResponse> {
  return secureFetch<MessageResponse>(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}/publish`, idToken, {
    method: 'POST',
  })
}

/**
 * Unpublish project (switches GSI3 → GSI4, decrements counts)
 */
export async function unpublishCraftProject(projectId: string, idToken: string): Promise<MessageResponse> {
  return secureFetch<MessageResponse>(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}/unpublish`, idToken, {
    method: 'POST',
  })
}

/**
 * Update craft project draft
 */
export async function updateCraftProject(
  projectId: string,
  projectData: UpdateCraftProjectRequest,
  idToken: string,
): Promise<SuccessResponse> {
  return secureFetch(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}/draft`, idToken, {
    method: 'PUT',
    body: JSON.stringify(projectData),
  })
}

/**
 * Get craft project
 */
export async function fetchCraftProject(projectId: string, idToken?: string): Promise<CraftProjectResponse> {
  if (idToken) {
    return secureFetch(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}`, idToken, {
      method: 'GET',
    })
  } else {
    // Public access for non-authenticated users
    const response = await fetch(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}`)
    if (!response.ok) {
      throw new Error('Project not found')
    }
    return response.json() as Promise<CraftProjectResponse>
  }
}



/**
 * Archive craft project (removes from GSI2 active projects)
 */
export async function archiveCraftProject(projectId: string, idToken: string): Promise<void> {
  return secureFetch(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}/archive`, idToken, {
    method: 'POST',
  })
}

/**
 * Unarchive craft project (adds back to GSI2 active projects)
 */
export async function unarchiveCraftProject(projectId: string, idToken: string): Promise<void> {
  return secureFetch(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}/unarchive`, idToken, {
    method: 'POST',
  })
}

/**
 * Get craft room privacy settings
 */
export async function fetchCraftRoomSettings(idToken: string): Promise<CraftRoomSettingsResponse> {
  return secureFetch(`${SECURE_API_BASE}/craft-room/settings`, idToken, { method: 'GET' })
}

/**
 * Update craft room privacy settings
 */
export async function updateCraftRoomSettings(
  settings: {
    is_public?: boolean
    allow_material_discovery?: boolean
    show_purchase_info?: boolean
    show_material_checker?: boolean
  },
  idToken: string,
): Promise<void> {
  return secureFetch(`${SECURE_API_BASE}/craft-room/settings`, idToken, {
    method: 'PUT',
    body: JSON.stringify(settings),
  })
}

// Upload functions available via secure/images.ts unified upload:
// - uploadCraftRoomImage
// - uploadMaterialImage
// - uploadTempCraftProjectImage
// - uploadCraftProjectImage
// - uploadTempMaterialImage

/**
 * Get craft project materials
 */
export async function fetchProjectMaterials(projectId: string, idToken: string): Promise<ProjectMaterialResponse[]> {
  return secureFetch(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}/materials`, idToken, {
    method: 'GET',
  })
}

/**
 * Add material to craft project
 */
export async function addProjectMaterial(
  projectId: string,
  materialData: CreateProjectMaterialRequest,
  idToken: string,
): Promise<ProjectMaterialResponse> {
  return secureFetch(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}/materials`, idToken, {
    method: 'POST',
    body: JSON.stringify(materialData),
  })
}

/**
 * Update craft project material
 */
export async function updateProjectMaterial(
  projectId: string,
  materialId: string,
  materialData: UpdateProjectMaterialRequest,
  idToken: string,
): Promise<ProjectMaterialResponse> {
  return secureFetch(
    `${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}/materials/${encodeURIComponent(materialId)}`,
    idToken,
    {
      method: 'PUT',
      body: JSON.stringify(materialData),
    },
  )
}

/**
 * Delete craft project material
 */
export async function deleteProjectMaterial(projectId: string, materialId: string, idToken: string): Promise<void> {
  return secureFetch(
    `${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}/materials/${encodeURIComponent(materialId)}`,
    idToken,
    {
      method: 'DELETE',
    },
  )
}

/**
 * Get craft project comments
 */
export async function fetchProjectComments(projectId: string): Promise<ProjectCommentResponse[]> {
  return secureFetch(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}/comments`, '', {
    method: 'GET',
  })
}

/**
 * Add comment to craft project (requires authentication)
 */
export async function addProjectComment(
  projectId: string,
  content: string,
  idToken: string,
): Promise<ProjectCommentResponse> {
  return secureFetch(`${SECURE_API_BASE}/craft-projects/${encodeURIComponent(projectId)}/comments`, idToken, {
    method: 'POST',
    body: JSON.stringify({ content }),
  })
}

/**
 * Delete project comment (owner only)
 */
export async function deleteProjectComment(projectId: string, commentId: string, idToken: string): Promise<void> {
  return secureFetch(
    `${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}/comments/${encodeURIComponent(commentId)}`,
    idToken,
    {
      method: 'DELETE',
    },
  )
}

/**
 * Get single material details (public API - no auth required)
 */
export async function fetchMaterial(materialId: string): Promise<MaterialResponse> {
  const resp = await fetch(`${API_BASE}/materials/${encodeURIComponent(materialId)}`)
  if (!resp.ok) throw new Error('Material not found')
  return resp.json() as Promise<MaterialResponse>
}

/**
 * Search materials (public + private combined)
 */
export async function searchMaterials(
  query: string,
  idToken: string,
  limit: number = 20,
  includePersonal: boolean = true,
): Promise<MaterialSearchResponse> {
  const params = new URLSearchParams({ q: query, limit: limit.toString() })
  if (!includePersonal) params.set('includePersonal', 'false')
  return secureFetch(`${SECURE_API_BASE}/materials/search?${params}`, idToken, {
    method: 'GET',
  })
}
// Moved to secure/moderation.ts

// Moved to secure/profile.ts

// ---- MATERIAL BATCH OPERATIONS ----

/**
 * Batch update multiple materials
 */
export async function batchUpdateMaterials(
  changes: Record<string, Partial<CreateMaterialRequest>>,
  idToken: string,
): Promise<BatchUpdateResponse> {
  return secureFetch(`${SECURE_API_BASE}/materials/batch-update`, idToken, {
    method: 'POST',
    body: JSON.stringify({ changes }),
  })
}

/**
 * Bulk delete multiple materials
 */
export async function bulkDeleteMaterials(materialIds: string[], idToken: string): Promise<BulkDeleteResponse> {
  return secureFetch(`${SECURE_API_BASE}/materials/bulk-delete`, idToken, {
    method: 'POST',
    body: JSON.stringify({ materialIds }),
  })
}

/**
 * Delete craft project
 */
export async function deleteCraftProject(projectId: string, idToken: string): Promise<void> {
  return secureFetch(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}`, idToken, {
    method: 'DELETE',
  })
}

// ---- FOLDER MANAGEMENT ----

/**
 * Get user's folder configuration
 */
export async function getFolderConfig(idToken: string): Promise<FolderConfigResponse> {
  return secureFetch(`${SECURE_API_BASE}/folders/config`, idToken, {
    method: 'GET',
  })
}

/**
 * Update user's folder configuration
 */
export async function updateFolderConfig(
  folders: Array<{ name: string; icon?: string; color?: string }>,
  idToken: string,
): Promise<UpdateFolderConfigResponse> {
  return secureFetch(`${SECURE_API_BASE}/folders/config`, idToken, {
    method: 'PUT',
    body: JSON.stringify({ folders }),
  })
}

/**
 * Assign materials to folders
 */
export async function assignMaterialsToFolders(
  materialIds: string[],
  folderNames: string[],
  idToken: string,
): Promise<AssignFoldersResponse> {
  // Backend expects { assignments: Record<string, string[]> } where key is materialId and value is folders array
  const assignments: Record<string, string[]> = {}
  for (const materialId of materialIds) {
    assignments[materialId] = folderNames
  }
  return secureFetch(`${SECURE_API_BASE}/folders/assign`, idToken, {
    method: 'POST',
    body: JSON.stringify({ assignments }),
  })
}

// ---- INVITE REQUESTS ----
// Moved to secure/access.ts

// Moved to secure/feedback.ts

export async function fetchMaterialLinkSuggestions(idToken: string): Promise<MaterialLinkSuggestionQueueResponse> {
  return secureFetch<MaterialLinkSuggestionQueueResponse>(
    `${SECURE_API_BASE}/material-link-suggestions/moderation`,
    idToken,
    { method: 'GET' },
  )
}

export async function resolveMaterialLinkSuggestion(
  suggestionId: string,
  resolution: 'approved' | 'rejected',
  notes: string | undefined,
  idToken: string,
): Promise<ResolveMaterialLinkSuggestionResponse> {
  return secureFetch<ResolveMaterialLinkSuggestionResponse>(
    `${SECURE_API_BASE}/moderation/material-link-suggestions/${encodeURIComponent(suggestionId)}/moderate`,
    idToken,
    {
      method: 'POST',
      body: JSON.stringify({ action: resolution, moderator_notes: notes }),
    },
  )
}

// Moved to secure/patterns.ts

// Moved to secure/patterns.ts

/**
 * Merge duplicate materials (moderator only)
 */
export async function mergeDuplicateMaterials(
  oldMaterialId: string,
  newMaterialId: string,
  reason: string,
  idToken: string,
): Promise<MergeMaterialsResponse> {
  return secureFetch(`${SECURE_API_BASE}/moderation/materials/merge-duplicates`, idToken, {
    method: 'POST',
    body: JSON.stringify({
      old_material_id: oldMaterialId,
      new_material_id: newMaterialId,
      reason,
    }),
  })
}

/**
 * Merge materials by source/target (moderator only).
 */
export async function mergeMaterials(
  sourceMaterialId: string,
  targetMaterialId: string,
  idToken: string,
): Promise<MergeMaterialsResponse> {
  return secureFetch<MergeMaterialsResponse>(
    `${SECURE_API_BASE}/materials/merge`,
    idToken,
    {
      method: 'POST',
      body: JSON.stringify({
        source_material_id: sourceMaterialId,
        target_material_id: targetMaterialId,
      }),
    },
  )
}

// Moved to secure/comments.ts

// Moved to secure/moderation.ts

// Moved to secure/patterns.ts

// Moved to secure/moderation.ts

// Moved to secure/patterns.ts

// Moved to secure/social.ts

// Moved to secure/collections.ts

// ---- INVENTORY CSV EXPORT/IMPORT ----

// package-local: ExportOptions is frontend-only (CSV export form state)
export interface ExportOptions {
  exportType: 'all' | 'selected'
  columns: string[]
  selectedIds?: string[]
}

// package-local: ImportData is frontend-only (CSV import form state)
export interface ImportData {
  materials: Record<string, string>[]
  columnMapping: Record<string, string>
}

/**
 * Export inventory to CSV
 */
export async function exportInventoryCSV(options: ExportOptions, idToken: string): Promise<ExportCSVResponse> {
  const body = {
    columns: options.columns.map((id) => ({ id, label: id })),
    selectedIds: options.exportType === 'selected' ? options.selectedIds : undefined,
  }

  return secureFetch(`${SECURE_API_BASE}/inventory/export`, idToken, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

/**
 * Import inventory from CSV
 */
export async function importInventoryCSV(data: ImportData, idToken: string): Promise<ImportCSVResponse> {
  return secureFetch(`${SECURE_API_BASE}/inventory/import`, idToken, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

/**
 * Download CSV file (client-side utility)
 */
export function downloadCSV(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', filename)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
}

/**
 * Parse CSV file (client-side utility)
 */
export function parseCSVFile(file: File): Promise<{ headers: string[]; data: Record<string, string>[] }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (e) => {
      try {
        const text = e.target?.result as string
        const lines = text.split('\n').filter((line) => line.trim())

        if (lines.length === 0) {
          reject(new Error('Empty CSV file'))
          return
        }

        const headers = lines[0]!.split(',').map((h) => h.trim().replace(/"/g, ''))
        const data = lines.slice(1).map((line) => {
          const values = line.split(',').map((v) => v.trim().replace(/"/g, ''))
          const row: Record<string, string> = {}
          headers.forEach((header, index) => {
            row[header] = values[index] || ''
          })
          return row
        })

        resolve({ headers, data })
      } catch (error: unknown) {
        reject(error)
      }
    }

    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsText(file)
  })
}

export async function submitFieldSuggestion(
  data: {
    request_type: 'add' | 'remove' | 'modify'
    category: string
    subcategory?: string
    field_name: string
    modify_suggestion?: string
    additional_info?: string
  },
  idToken: string,
): Promise<SubmitFieldSuggestionResponse> {
  return secureFetch(`${SECURE_API_BASE}/field-suggestions`, idToken, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function dismissFieldSuggestion(suggestionId: string, idToken: string): Promise<void> {
  await secureFetch(`${SECURE_API_BASE}/field-suggestions/${encodeURIComponent(suggestionId)}`, idToken, {
    method: 'DELETE',
  })
}

/**
 * Resolve a field suggestion as implemented or planned (Req 11.1, 11.2).
 * POST /field-suggestions/{id}/resolve
 */
export async function resolveFieldSuggestion(
  id: string,
  body: ResolveFieldSuggestionRequest,
  idToken: string,
): Promise<SimpleSuccessResponse> {
  return secureFetch<SimpleSuccessResponse>(
    `${SECURE_API_BASE}/field-suggestions/${encodeURIComponent(id)}/resolve`,
    idToken,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  )
}

/**
 * Change which public material a personal material is linked to (Req 21.3 / J7).
 * POST /craft-room/materials/{materialId}/change-link
 *
 * Re-runs the link logic with the new public material ID, updating all pattern
 * drafts, project drafts, and material usage records. There is no unlink option.
 */
export async function changeMaterialLink(
  materialId: string,
  body: ChangeLinkRequest,
  idToken: string,
): Promise<SimpleSuccessResponse> {
  return secureFetch<SimpleSuccessResponse>(
    `${SECURE_API_BASE}/craft-room/materials/${encodeURIComponent(materialId)}/change-link`,
    idToken,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  )
}

export async function getFieldSuggestionMessages(suggestionId: string, idToken: string): Promise<FieldSuggestionThread> {
  return secureFetch(`${SECURE_API_BASE}/field-suggestions/${encodeURIComponent(suggestionId)}/messages`, idToken, {
    method: 'GET',
  })
}

export async function sendModeratorMessage(suggestionId: string, content: string, idToken: string): Promise<SendFieldSuggestionMessageResponse> {
  return secureFetch(`${SECURE_API_BASE}/field-suggestions/${encodeURIComponent(suggestionId)}/message`, idToken, {
    method: 'POST',
    body: JSON.stringify({ content }),
  })
}

export async function sendUserReply(suggestionId: string, content: string, idToken: string): Promise<SendFieldSuggestionMessageResponse> {
  return secureFetch(`${SECURE_API_BASE}/field-suggestions/${encodeURIComponent(suggestionId)}/reply`, idToken, {
    method: 'POST',
    body: JSON.stringify({ content }),
  })
}

/**
 * Fetch pending link confirmations for the current user (owner).
 */
export async function fetchPendingLinkConfirmations<T = PendingConfirmationsResponse>(idToken: string): Promise<T> {
  return secureFetch<T>(
    `${SECURE_API_BASE}/material-link-suggestions/pending`,
    idToken,
    { method: 'GET' },
  )
}

/**
 * Accept or reject a pending link confirmation.
 */
export async function confirmLinkSuggestion(
  suggestionId: string,
  action: 'accept' | 'reject',
  idToken: string,
): Promise<SuccessResponse> {
  return secureFetch<SuccessResponse>(
    `${SECURE_API_BASE}/material-link-suggestions/${encodeURIComponent(suggestionId)}/confirm`,
    idToken,
    {
      method: 'POST',
      body: JSON.stringify({ action }),
    },
  )
}

/**
 * Submit a create-parent material contribution proposal.
 */
export async function submitCreateParentContribution(
  data: {
    suggested_parent_name: string
    description: string
    variation_ids: string[]
    brand: string
    category: string
  },
  idToken: string,
): Promise<ContributeMaterialResponse> {
  return secureFetch<ContributeMaterialResponse>(
    `${SECURE_API_BASE}/materials/contribute`,
    idToken,
    {
      method: 'POST',
      body: JSON.stringify({
        submission_type: 'create_parent',
        parent_request: data,
        material_data: {},
      }),
    },
  )
}
